import React, { useEffect, useState } from 'react';
import { getMyStore, saveStoreConfig } from '../../services/api';
import { Input, Textarea, Btn, toast, Card, Spinner } from '../../components/shared/UI';

/**
 * Config fields que guarda el PHP:
 * { nombre, descripcion, color, moneda, whatsapp, mp_access_token }
 */
const DEFAULTS = {
  nombre: '',
  descripcion: '',
  color: '#7c3aed',
  moneda: 'ARS',
  whatsapp: '',
  mp_access_token: '',
  mp_public_key: '',
  mp_access_token_configured: false,
};

export default function ClientConfig() {
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // GET api/client/store → { config, productos }
    getMyStore()
      .then(s => setConfig({ ...DEFAULTS, ...s.config }))
      .catch(() => {});
  }, []);

  const set = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // PUT api/client/store/config
      await saveStoreConfig(config);
      toast('Configuración guardada ✅');
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!config) return (
    <div style={{ display:'flex',justifyContent:'center',padding:60 }}>
      <Spinner />
    </div>
  );

  return (
    <div style={{ maxWidth:620 }}>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ color:'#fff',fontSize:24,fontFamily:"'Syne',sans-serif",fontWeight:800,margin:'0 0 6px' }}>
          Configuración
        </h1>
        <p style={{ color:'rgba(255,255,255,0.38)',fontSize:14,margin:0 }}>
          Personalizá el aspecto y los datos de tu tienda.
        </p>
      </div>

      <form onSubmit={handleSubmit}>

        {/* Datos generales */}
        <Card style={{ marginBottom:16 }}>
          <h2 style={{ color:'#fff',fontSize:15,fontWeight:600,margin:'0 0 20px' }}>🏪 Datos de la tienda</h2>
          <Input
            label="Nombre de la tienda *"
            value={config.nombre}
            onChange={e => set('nombre', e.target.value)}
            placeholder="Zapatillas Luna"
            required
          />
          <Textarea
            label="Descripción"
            value={config.descripcion}
            onChange={e => set('descripcion', e.target.value)}
            placeholder="Contale a tus clientes de qué trata tu tienda..."
          />
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <Input
              label="Moneda"
              value={config.moneda}
              onChange={e => set('moneda', e.target.value.toUpperCase())}
              placeholder="ARS"
              maxLength={5}
            />
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block',color:'rgba(255,255,255,0.55)',fontSize:13,fontWeight:500,marginBottom:6 }}>
                Color principal
              </label>
              <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                <input
                  type="color"
                  value={config.color}
                  onChange={e => set('color', e.target.value)}
                  style={{ width:46,height:40,padding:2,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,cursor:'pointer' }}
                />
                <span style={{ color:'rgba(255,255,255,0.4)',fontSize:13 }}>{config.color}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* WhatsApp */}
        <Card style={{ marginBottom:16 }}>
          <h2 style={{ color:'#fff',fontSize:15,fontWeight:600,margin:'0 0 8px' }}>📱 WhatsApp</h2>
          <p style={{ color:'rgba(255,255,255,0.35)',fontSize:13,margin:'0 0 16px',lineHeight:1.5 }}>
            Los clientes van a poder contactarte directamente. Ingresá el número con código de país, sin el signo +.
          </p>
          <Input
            label="Número de WhatsApp"
            value={config.whatsapp}
            onChange={e => set('whatsapp', e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="5491122334455"
            type="tel"
          />
          <p style={{ color:'rgba(255,255,255,0.25)',fontSize:12,margin:'-8px 0 0' }}>
            Ejemplo: Argentina +54 911 2233 4455 → <code style={{ color:'rgba(255,255,255,0.4)' }}>5491122334455</code>
          </p>
        </Card>

        {/* MercadoLibre / MercadoPago */}
        <Card style={{ marginBottom:24 }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:4 }}>
            <h2 style={{ color:'#fff',fontSize:15,fontWeight:600,margin:0 }}>💳 MercadoLibre / MercadoPago</h2>
            {config.mp_access_token_configured && (
              <span style={{
                fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,
                background: config.mp_public_key?.startsWith('TEST-') || config.mp_access_token?.startsWith('TEST-')
                  ? 'rgba(234,179,8,0.15)' : 'rgba(34,197,94,0.15)',
                color: config.mp_public_key?.startsWith('TEST-') || config.mp_access_token?.startsWith('TEST-')
                  ? '#fde047' : '#4ade80',
                border: config.mp_public_key?.startsWith('TEST-') || config.mp_access_token?.startsWith('TEST-')
                  ? '1px solid rgba(234,179,8,0.3)' : '1px solid rgba(34,197,94,0.3)',
              }}>
                {config.mp_public_key?.startsWith('TEST-')
                  ? '🧪 Modo TEST'
                  : '✅ Modo PRODUCCIÓN'}
              </span>
            )}
          </div>

          <p style={{ color:'rgba(255,255,255,0.35)',fontSize:13,margin:'0 0 18px',lineHeight:1.5 }}>
            Conseguí las credenciales en{' '}
            <a href="https://developers.mercadolibre.com.ar/devcenter" target="_blank" rel="noreferrer" style={{ color:'#a78bfa' }}>
              developers.mercadolibre.com.ar
            </a>{' '}→ Tu aplicación → Credenciales.
            Para probar usá las de <strong style={{ color:'rgba(255,255,255,0.55)' }}>TEST</strong>, para cobrar de verdad usá las de <strong style={{ color:'rgba(255,255,255,0.55)' }}>Producción</strong>.
          </p>

          {/* Access Token */}
          <div style={{ marginBottom:6 }}>
            <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:6 }}>
              <label style={{ color:'rgba(255,255,255,0.55)',fontSize:13,fontWeight:500 }}>
                Access Token (Token de acceso)
              </label>
              {config.mp_access_token_configured && !config.mp_access_token && (
                <span style={{ fontSize:11,color:'#4ade80',background:'rgba(34,197,94,0.12)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:12,padding:'2px 8px' }}>
                  ✓ Guardado
                </span>
              )}
            </div>
            <Input
              label=""
              type="password"
              value={config.mp_access_token}
              onChange={e => set('mp_access_token', e.target.value)}
              placeholder={config.mp_access_token_configured ? '••••••••  (dejar vacío para conservar el actual)' : 'APP_USR-...  o  TEST-...'}
            />
          </div>
          <p style={{ color:'rgba(255,255,255,0.25)',fontSize:12,margin:'-4px 0 16px' }}>
            Clave privada del servidor. <strong style={{ color:'rgba(255,255,255,0.4)' }}>Nunca</strong> se muestra a los compradores. Si empieza con <code style={{ color:'#fde047' }}>TEST-</code> activa el modo sandbox.
          </p>

          {/* Public Key */}
          <Input
            label="Public Key (Clave pública)"
            value={config.mp_public_key}
            onChange={e => set('mp_public_key', e.target.value)}
            placeholder={config.mp_access_token_configured ? 'APP_USR-...  o  TEST-...' : 'APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
          />
          <p style={{ color:'rgba(255,255,255,0.25)',fontSize:12,margin:'-10px 0 0' }}>
            Clave pública para el checkout del comprador. Usá siempre el mismo modo (TEST o producción) que el Access Token.
          </p>

          {/* Aviso modo TEST */}
          {config.mp_public_key?.startsWith('TEST-') && (
            <div style={{
              marginTop:14,padding:'12px 14px',borderRadius:10,
              background:'rgba(234,179,8,0.08)',border:'1px solid rgba(234,179,8,0.25)',
            }}>
              <p style={{ color:'#fde047',fontSize:12,margin:0,lineHeight:1.6 }}>
                🧪 <strong>Modo TEST activo.</strong> Para probar el pago usá una cuenta de comprador de prueba de MercadoPago
                (creala en <a href="https://developers.mercadolibre.com.ar/devcenter" target="_blank" rel="noreferrer" style={{ color:'#fde047' }}>Dev Center</a> → Cuentas de prueba).
                No uses tu cuenta real o verás el error "una de las partes es de prueba".
              </p>
            </div>
          )}
        </Card>

        <div style={{ display:'flex',justifyContent:'flex-end' }}>
          <Btn type="submit" size="lg" loading={loading}>
            Guardar cambios
          </Btn>
        </div>
      </form>
    </div>
  );
}
